import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Calendar,
  Clock,
  MapPin,
  Navigation,
  Gauge,
  Route,
  CircleDot,
} from "lucide-react";
import {
  mockTrips,
  mockLocationHistory,
  formatDistance,
  formatSpeed,
  formatDuration,
  formatRelativeTime,
} from "@/lib/mockData";
import { TRIP_STATUS } from "@/lib/constants";
import GoogleMap from "@/components/GoogleMap";

function TripStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();

  const getStatusConfig = (status: string) => {
    switch (status) {
      case TRIP_STATUS.ACTIVE:
        return {
          label: t("tripActive"),
          variant: "default" as const,
          className: "bg-green-500 hover:bg-green-600",
        };
      case TRIP_STATUS.COMPLETED:
        return {
          label: t("tripCompleted"),
          variant: "secondary" as const,
          className: "",
        };
      case TRIP_STATUS.PAUSED:
        return {
          label: t("tripPaused"),
          variant: "outline" as const,
          className: "border-yellow-500 text-yellow-700",
        };
      default:
        return {
          label: status,
          variant: "outline" as const,
          className: "",
        };
    }
  };

  const config = getStatusConfig(status);

  return (
    <Badge variant={config.variant} className={config.className}>
      {config.label}
    </Badge>
  );
}

function TripCard({ trip }: { trip: (typeof mockTrips)[0] }) {
  const { t } = useTranslation();
  const isActive = trip.status === TRIP_STATUS.ACTIVE;
  const duration = trip.duration
    ? `${Math.floor(trip.duration / 60)} ${t("minutes")}`
    : formatDuration(trip.startTime, trip.endTime);

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Route className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium text-sm">
              {isActive
                ? "Current Trip"
                : `Trip on ${trip.startTime.toLocaleDateString()}`}
            </span>
          </div>
          <TripStatusBadge status={trip.status} />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <div>
              <div className="font-medium">{t("tripStarted")}</div>
              <div className="text-muted-foreground">
                {trip.startTime.toLocaleDateString()}{" "}
                {trip.startTime.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </div>
            </div>
          </div>

          {trip.endTime && (
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <div className="font-medium">{t("tripEnded")}</div>
                <div className="text-muted-foreground">
                  {trip.endTime.toLocaleDateString()}{" "}
                  {trip.endTime.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div className="flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <div>
              <div className="font-medium">{t("duration")}</div>
              <div className="text-muted-foreground">{duration}</div>
            </div>
          </div>

          {trip.distance && (
            <div className="flex items-center gap-2 text-sm">
              <Navigation className="h-4 w-4 text-muted-foreground" />
              <div>
                <div className="font-medium">{t("distance")}</div>
                <div className="text-muted-foreground">
                  {formatDistance(trip.distance)}
                </div>
              </div>
            </div>
          )}

          {trip.averageSpeed && (
            <div className="flex items-center gap-2 text-sm">
              <Gauge className="h-4 w-4 text-muted-foreground" />
              <div>
                <div className="font-medium">{t("averageSpeed")}</div>
                <div className="text-muted-foreground">
                  {formatSpeed(trip.averageSpeed)}
                </div>
              </div>
            </div>
          )}

          {trip.maxSpeed && (
            <div className="flex items-center gap-2 text-sm">
              <Gauge className="h-4 w-4 text-muted-foreground" />
              <div>
                <div className="font-medium">{t("maxSpeed")}</div>
                <div className="text-muted-foreground">
                  {formatSpeed(trip.maxSpeed)}
                </div>
              </div>
            </div>
          )}
        </div>

        {isActive && (
          <div className="mt-3 p-3 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
            <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-300">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="font-medium">
                {t("tripActive")} - {duration}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function LocationTimelineItem({
  locationPoint,
  isFirst,
  isLast,
}: {
  locationPoint: (typeof mockLocationHistory)[0];
  isFirst: boolean;
  isLast: boolean;
}) {
  const { t } = useTranslation();
  const relativeTime = formatRelativeTime(locationPoint.timestamp);
  const hasSpeed = locationPoint.location.speed !== undefined;

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div
          className={`w-3 h-3 rounded-full ${isFirst ? "bg-green-500" : "bg-blue-500"}`}
        />
        {!isLast && <div className="w-0.5 h-8 bg-border mt-1" />}
      </div>
      <div className="flex-1 pb-6">
        <div className="flex items-center justify-between mb-1">
          <div className="font-medium text-sm">
            {isFirst ? t("currentLocation") : relativeTime}
          </div>
          <div className="text-xs text-muted-foreground">
            {locationPoint.timestamp.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        </div>
        <div className="text-sm text-muted-foreground mb-2">
          {locationPoint.location.latitude.toFixed(6)},{" "}
          {locationPoint.location.longitude.toFixed(6)}
        </div>
        {hasSpeed &&
          locationPoint.location.speed &&
          locationPoint.location.speed > 0 && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Gauge className="h-3 w-3" />
              <span>{formatSpeed(locationPoint.location.speed)}</span>
            </div>
          )}
      </div>
    </div>
  );
}

export default function LocationHistory() {
  const { t } = useTranslation();
  const trips = mockTrips;
  const locationHistory = mockLocationHistory;
  const activeTrips = trips.filter((t) => t.status === TRIP_STATUS.ACTIVE);
  const completedTrips = trips.filter((t) => t.status !== TRIP_STATUS.ACTIVE);
  const totalDistance = trips.reduce(
    (sum, trip) => sum + (trip.distance || 0),
    0,
  );
  const currentLocation = locationHistory[0];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          {t("locationHistory")}
        </h1>
        <p className="text-muted-foreground">
          {t("locationHistoryDescription")}
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <Route className="h-4 w-4 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("tripActive")}
                </p>
                <p className="text-2xl font-bold">{activeTrips.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Navigation className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("distance")}
                </p>
                <p className="text-2xl font-bold">
                  {formatDistance(totalDistance)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <MapPin className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("tripHistory")}
                </p>
                <p className="text-2xl font-bold">{trips.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Location Timeline */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                {t("locationTimeline")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {locationHistory.length > 0 ? (
                <div className="space-y-2">
                  {locationHistory.map((point, index) => (
                    <LocationTimelineItem
                      key={point.id}
                      locationPoint={point}
                      isFirst={index === 0}
                      isLast={index === locationHistory.length - 1}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {t("noLocationHistory")}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Current Location */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CircleDot className="h-5 w-5 text-green-500" />
                {t("currentLocation")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {currentLocation ? (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="font-medium text-sm text-green-700 dark:text-green-300">
                        Live Location
                      </span>
                    </div>
                    <div className="text-sm text-muted-foreground mb-1">
                      {currentLocation.location.latitude.toFixed(6)},{" "}
                      {currentLocation.location.longitude.toFixed(6)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t("lastUpdated")}:{" "}
                      {formatRelativeTime(currentLocation.timestamp)}
                    </div>
                  </div>

                  {currentLocation.location.speed !== undefined && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center gap-2 text-sm">
                        <Gauge className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{t("speed")}</div>
                          <div className="text-muted-foreground">
                            {formatSpeed(currentLocation.location.speed)}
                          </div>
                        </div>
                      </div>

                      {currentLocation.location.accuracy && (
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <div className="font-medium">Accuracy</div>
                            <div className="text-muted-foreground">
                              ±{currentLocation.location.accuracy} {t("meters")}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Google Map showing current location and important places */}
                  <div className="mt-4">
                    <GoogleMap
                      currentLocation={
                        currentLocation
                          ? {
                              lat: currentLocation.location.latitude,
                              lng: currentLocation.location.longitude,
                            }
                          : undefined
                      }
                      height="250px"
                      zoom={14}
                    />
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    No current location available
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Active Trips */}
      {activeTrips.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold mb-4">Active Trips</h2>
          <div className="space-y-4">
            {activeTrips.map((trip) => (
              <TripCard key={trip.id} trip={trip} />
            ))}
          </div>
        </div>
      )}

      {/* Recent Trips */}
      <div>
        <h2 className="text-xl font-semibold mb-4">
          {activeTrips.length > 0 ? "Recent Trips" : t("tripHistory")}
        </h2>
        {completedTrips.length > 0 ? (
          <div className="space-y-4">
            {completedTrips.map((trip) => (
              <TripCard key={trip.id} trip={trip} />
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <Route className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">{t("noTrips")}</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
